import { Injectable } from '@angular/core';

/**
 * Service to manage a detached browser window for previewing HTML content.
 * Implements singleton pattern for the popup window.
 */
@Injectable({
  providedIn: 'root',
})
export class HtmlPreviewService {
  private _popupWindow: Window | null = null;

  private readonly _windowFeatures =
    'width=800,height=600,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes';

  /**
   * Opens or updates the HTML preview window with the provided content.
   * - If the window is already open, updates its content.
   * - If the window is closed or null, opens a new one.
   *
   * @param htmlContent - The HTML content to display in the preview window
   */
  public preview(htmlContent: string): void {
    if (this._isWindowOpen()) {
      this._updateContent(htmlContent);
    } else {
      this._openNewWindow(htmlContent);
    }
  }

  /**
   * Updates the preview window content only if it's already open.
   * Does nothing if the window is closed or null.
   * Used for auto-updating during streaming.
   *
   * @param htmlContent - The HTML content to display in the preview window
   */
  public updateIfOpen(htmlContent: string): void {
    if (this._isWindowOpen()) {
      this._updateContent(htmlContent);
    }
  }

  /**
   * Closes the preview window if it's open.
   */
  public close(): void {
    if (this._isWindowOpen()) {
      this._popupWindow.close();
      this._popupWindow = null;
    }
  }

  /**
   * Checks if the preview window is currently open.
   */
  public isOpen(): boolean {
    return this._isWindowOpen();
  }

  /**
   * Checks if the popup window reference is valid and the window is open.
   */
  private _isWindowOpen(): boolean {
    return this._popupWindow !== null && !this._popupWindow.closed;
  }

  /**
   * Opens a new popup window and writes the HTML content.
   */
  private _openNewWindow(htmlContent: string): void {
    this._popupWindow = window.open('', 'HtmlPreview', this._windowFeatures);

    if (this._popupWindow) {
      this._writeContent(htmlContent);
      this._popupWindow.focus();
    }
  }

  /**
   * Updates the content of an existing popup window.
   */
  private _updateContent(htmlContent: string): void {
    if (this._popupWindow) {
      this._writeContent(htmlContent);
      this._popupWindow.focus();
    }
  }

  /**
   * Writes the HTML content to the popup window's document.
   */
  private _writeContent(htmlContent: string): void {
    if (!this._popupWindow) {
      return;
    }

    const doc = this._popupWindow.document;

    doc.open();
    doc.write(htmlContent);
    doc.close();
  }
}
